import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCoinbaseOnrampOptions } from "../../../../anyspend/react/index.js";
import { ALL_CHAINS } from "../../../../anyspend/utils/chain.js";
import { Input, toast, useGetGeo, useProfile } from "../../../../global-account/react/index.js";
import { cn, formatUsername } from "../../../../shared/utils/index.js";
import { formatAddress } from "../../../../shared/utils/formatAddress.js";
import { ChevronRight, Info, Wallet } from "lucide-react";
import { useRef } from "react";
import { FIAT_PAYMENT_METHOD_DISPLAY, FiatPaymentMethod } from "./FiatPaymentMethod.js";
import { OrderTokenAmountFiat } from "./OrderTokenAmountFiat.js";
import { PointsBadge } from "./PointsBadge.js";
const ONE_CHAR_WIDTH = 30;
export function PanelOnramp({
  srcAmountOnRamp,
  setSrcAmountOnRamp,
  selectedPaymentMethod,
  setActivePanel,
  _recipientAddress,
  destinationToken,
  destinationChainId,
  destinationAmount,
  onDestinationTokenChange,
  onDestinationChainChange,
  fiatPaymentMethodIndex,
  recipientSelectionPanelIndex,
  dstTokenSymbol,
  hideDstToken = false,
  anyspendQuote,
  onShowPointsDetail,
  onShowFeeDetail,
  customUsdInputValues = ["5", "10", "20", "25"],
  customRecipientLabel,
  classes
}) {
  // Helper function to get fees from anyspend quote
  const getFeeFromApi = paymentMethod => {
    // Try to get fee from anyspend quote first (most accurate)
    if (anyspendQuote?.data?.fee) {
      const fee = anyspendQuote.data.fee;
      if (fee.type === "stripeweb2_fee") {
        // Calculate total fee in USD from originalAmount - finalAmount
        const originalAmount = Number(fee.originalAmount) / 1e6; // Convert from wei to USD
        const finalAmount = Number(fee.finalAmount) / 1e6;
        return originalAmount - finalAmount;
      }
    }
    // Fallback to payment method defaults
    switch (paymentMethod) {
      case FiatPaymentMethod.COINBASE_PAY:
        return 0;
      // Coinbase has no additional fees
      case FiatPaymentMethod.STRIPE:
        return null;
      // No quote available yet
      default:
        return null;
    }
  };
  // Helper function to get total amount from API (for Stripe) or calculate it (for others)
  const getTotalAmount = paymentMethod_0 => {
    const baseAmount = parseFloat(srcAmountOnRamp) || 5;
    // For stripeweb2_fee, use the originalAmount
    if (anyspendQuote?.data?.fee?.type === "stripeweb2_fee") {
      return Number(anyspendQuote.data.fee.originalAmount) / 1e6; // Convert from wei to USD
    }
    // Use currencyIn.amountUsd from quote when available (includes fees, most accurate for custom orders)
    if (anyspendQuote?.data?.currencyIn?.amountUsd) {
      return Number(anyspendQuote.data.currencyIn.amountUsd);
    }
    const fee_0 = getFeeFromApi(paymentMethod_0);
    // For Coinbase or when fee is available, calculate manually
    if (fee_0 !== null) {
      return baseAmount + fee_0;
    }
    // No fee available, return base amount
    return baseAmount;
  };
  // Get geo data for onramp availability
  const {
    geoData
  } = useGetGeo();
  const {
    coinbaseOnrampOptions
  } = useCoinbaseOnrampOptions(geoData?.country || "US");
  // Get recipient profile for displaying name
  const recipientProfile = useProfile({
    address: _recipientAddress
  });
  const recipientName = recipientProfile.data?.name;
  const amountInputRef = useRef(null);
  const handleAmountChange = e => {
    // Only allow numbers and decimal points
    const value = e.target.value.replace(/[^0-9.]/g, "");
    // Get the max limit from payment currencies
    const maxLimit = coinbaseOnrampOptions?.paymentCurrencies?.[0]?.limits?.find(l => l.id === "ACH_BANK_ACCOUNT")?.max || "25000";
    const numValue = parseFloat(value);
    if (!Number.isNaN(numValue) && numValue > parseFloat(maxLimit)) {
      toast.error(`Maximum amount is $${maxLimit}`);
      setSrcAmountOnRamp(maxLimit);
      return;
    }
    setSrcAmountOnRamp(value);
  };
  const handleQuickAmount = value_0 => {
    setSrcAmountOnRamp(value_0);
  };
  return _jsxs("div", {
    className: classes?.container || "panel-onramp bg-as-surface-primary flex w-full flex-col",
    children: [_jsxs("div", {
      className: "border-as-border-secondary bg-as-surface-secondary relative flex w-full flex-col rounded-2xl border p-4",
      children: [_jsxs("div", {
        className: "flex h-7 w-full items-center justify-between",
        children: [_jsx("span", {
          className: "text-as-tertiary flex items-center text-sm font-bold",
          children: "Pay"
        }), _jsx("button", {
          className: "text-as-tertiary flex h-7 items-center gap-1 text-sm",
          onClick: () => setActivePanel(fiatPaymentMethodIndex),
          children: (() => {
            const config = selectedPaymentMethod ? FIAT_PAYMENT_METHOD_DISPLAY[selectedPaymentMethod] : null;
            if (config) {
              return _jsxs(_Fragment, {
                children: [_jsxs("div", {
                  className: "flex items-center gap-2",
                  children: [_jsx("div", {
                    className: "flex h-5 w-5 items-center justify-center rounded-full bg-blue-600",
                    children: _jsx("span", {
                      className: "text-xs font-bold text-white",
                      children: config.icon
                    })
                  }), config.label]
                }), _jsx(ChevronRight, {
                  className: "h-4 w-4"
                })]
              });
            }
            return _jsxs(_Fragment, {
              children: ["Select payment method", _jsx(ChevronRight, {
                className: "h-4 w-4"
              })]
            });
          })()
        })]
      }), _jsx("div", {
        className: "flex items-center justify-center pb-2 pt-8",
        children: _jsxs("div", {
          className: "flex gap-1",
          children: [_jsx("span", {
            className: "text-as-tertiary text-2xl font-bold",
            children: "$"
          }), _jsx(Input, {
            ref: amountInputRef,
            type: "text",
            value: srcAmountOnRamp,
            onChange: handleAmountChange,
            placeholder: "5",
            className: "text-as-primary placeholder:text-as-primary/50 h-auto border-0 bg-transparent p-0 px-1 pt-1 text-4xl font-bold focus-visible:ring-0 focus-visible:ring-offset-0",
            style: {
              width: `${Math.max(ONE_CHAR_WIDTH, srcAmountOnRamp.length * ONE_CHAR_WIDTH)}px`
            }
          })]
        })
      }), _jsx("div", {
        className: cn("mx-auto mb-6 flex justify-center gap-2", hideDstToken && "mb-0"),
        children: customUsdInputValues.filter(v => !isNaN(Number(v))).map(value_1 => _jsxs("button", {
          onClick: () => handleQuickAmount(value_1),
          className: `bg-as-surface-secondary border-as-border-secondary hover:border-as-border-secondary h-7 w-14 rounded-lg border text-sm font-medium transition-all duration-200 ${srcAmountOnRamp === value_1 ? "border-as-border-secondary bg-as-surface-secondary" : "bg-as-surface-secondary hover:bg-as-surface-secondary"}`,
          children: ["$", value_1]
        }, value_1))
      }), destinationToken && destinationChainId && !hideDstToken && _jsx(OrderTokenAmountFiat, {
        address: _recipientAddress,
        context: "to",
        inputValue: destinationAmount || "0",
        onChangeInput: () => {},
        chainId: destinationChainId,
        setChainId: onDestinationChainChange || (() => {}),
        token: destinationToken,
        setToken: onDestinationTokenChange || (() => {})
      })]
    }), _jsxs("div", {
      className: "border-as-border-secondary bg-as-surface-secondary mt-4 flex w-full flex-col gap-3 rounded-xl border p-4",
      children: [_jsxs("div", {
        className: "flex w-full items-center justify-between gap-2",
        children: [_jsx("span", {
          className: "text-as-tertiary flex items-center text-sm",
          children: "Recipient"
        }), _recipientAddress ? _jsxs("button", {
          className: "text-as-tertiary flex h-7 items-center gap-1 text-sm transition-colors hover:text-blue-700",
          onClick: () => setActivePanel(recipientSelectionPanelIndex),
          children: [_jsx("span", {
            className: "text-sm",
            children: customRecipientLabel || (recipientName ? formatUsername(recipientName) : formatAddress(_recipientAddress))
          }), _jsx(ChevronRight, {
            size: 16
          })]
        }) : _jsxs("button", {
          className: "text-as-tertiary flex h-7 items-center gap-1 text-sm transition-colors hover:text-blue-700",
          onClick: () => setActivePanel(5),
          children: [_jsx(Wallet, {
            className: "text-as-brand",
            size: 16
          }), "Select recipient", _jsx(ChevronRight, {
            size: 16
          })]
        })]
      }), _jsx("div", {
        className: "divider w-full"
      }), _jsxs("div", {
        className: "flex items-center justify-between gap-4",
        children: [_jsx("span", {
          className: "text-as-tertiary text-sm",
          children: "Expected to receive"
        }), _jsxs("div", {
          className: "flex flex-wrap items-center justify-end gap-1",
          children: [_jsxs("span", {
            className: "text-as-primary font-semibold",
            children: [destinationAmount || "0", " ", dstTokenSymbol || destinationToken?.symbol || ""]
          }), _jsxs("div", {
            className: "flex items-center gap-1",
            children: [_jsxs("span", {
              className: "text-as-tertiary text-sm",
              children: ["on ", destinationChainId ? ALL_CHAINS[destinationChainId]?.name : ""]
            }), destinationToken && destinationChainId && destinationToken.metadata?.logoURI && _jsx("img", {
              src: ALL_CHAINS[destinationChainId]?.logoUrl,
              alt: "Chain",
              className: "h-4 w-4 rounded-full"
            })]
          })]
        })]
      }), _jsx("div", {
        className: "divider w-full"
      }), _jsx("div", {
        className: "",
        children: _jsxs("div", {
          className: "flex items-center justify-between",
          children: [_jsxs("div", {
            className: "flex items-center gap-1.5",
            children: [_jsx("span", {
              className: "text-as-tertiary text-sm",
              children: "Total"
            }), anyspendQuote?.data?.fee && onShowFeeDetail && _jsx("button", {
              onClick: onShowFeeDetail,
              className: "text-as-primary/40 hover:text-as-primary/60 transition-colors",
              children: _jsx(Info, {
                className: "h-4 w-4"
              })
            }), anyspendQuote?.data?.pointsAmount && anyspendQuote?.data?.pointsAmount > 0 && _jsx(PointsBadge, {
              pointsAmount: anyspendQuote.data.pointsAmount,
              pointsMultiplier: anyspendQuote.data.pointsMultiplier,
              onClick: () => onShowPointsDetail?.()
            })]
          }), _jsxs("div", {
            className: "flex flex-col items-end gap-0.5",
            children: [_jsxs("span", {
              className: "text-as-primary font-semibold",
              children: ["$", getTotalAmount(selectedPaymentMethod || FiatPaymentMethod.NONE).toFixed(2)]
            }), (() => {
              // For fiat payments, show the fee from the payment method
              const fiatFee = getFeeFromApi(selectedPaymentMethod || FiatPaymentMethod.NONE);
              if (fiatFee !== null && fiatFee > 0) {
                return _jsxs("span", {
                  className: "text-as-secondary text-xs",
                  children: ["incl. $", fiatFee.toFixed(2), " fee"]
                });
              }
              // For crypto payments (standard_fee), calculate from the quote
              if (anyspendQuote?.data?.fee?.type === "standard_fee" && anyspendQuote.data.currencyIn?.amountUsd) {
                const cryptoFee = Number(anyspendQuote.data.currencyIn.amountUsd) * anyspendQuote.data.fee.finalFeeBps / 10000;
                if (cryptoFee > 0) {
                  return _jsxs("span", {
                    className: "text-as-secondary text-xs",
                    children: ["incl. $", cryptoFee.toFixed(2), " fee"]
                  });
                }
              }
              return null;
            })()]
          })]
        })
      })]
    })]
  });
}